function getErrorData(error) {
  if (error && error.response && error.response.data) return error.response.data
  if (error && typeof error === 'object') return error

  return {}
}

export default {
  namespaced: true,
  state: {
    // The Profile Qualifications currently visible
    active: [],
    // Used to indicate when waiting on the server
    loading: false,
    // The search string being applied
    search: '',
    // What filters are currently applied
    filters: {},
    // The total records matching the filter according to the server (for pagination)
    total: 0,
    // The current page being viewed and its size
    page: 1,
    rowsPerPage: 25,
    // Sorting
    sort: {
      by: 'updatedAt',
      descending: false,
    },
  },
  getters: {
    find: state => id => state.all.find(profileQualification => profileQualification.id == id),
  },
  mutations: {
    setActive(
      state,
      {
        items = [],
        page = 1,
        per = 25,
        total = 0,
        search = null,
        sort = { by: 'updatedAt', descending: true },
        filters = {},
      }
    ) {
      state.search = search
      state.active = items
      state.sort = sort
      state.filters = filters
      state.page = page
      state.rowsPerPage = per
      state.total = total
      state.loading = false
    },
    set(state, profileQualification) {
      state.active = state.active.filter(o => o.id != profileQualification.id)
      state.active.push(profileQualification)
    },
    remove(state, id) {
      state.active = state.active.filter(o => o.id != id)
    },
  },
  actions: {
    search(
      { commit, state },
      {
        search = '',
        page = 1,
        rowsPerPage = 25,
        sortBy = 'updatedAt',
        descending = true,
        filters = {},
      }
    ) {
      this.$authorize('list', 'ProfileQualification')

      const params = {
        search: search,
        page: page,
        per: rowsPerPage,
        sort: `${sortBy} ${descending ? 'DESC' : 'ASC'}`,
        ...filters,
      }

      state.loading = true
      return Vue.$http
        .get('admin/profile_qualifications', { params })
        .then(response => commit('setActive', response.data), error => (state.loading = false))
    },
    fetch({ commit }, id) {
      this.$authorize('read', 'ProfileQualification')

      return Vue.$http
        .get(`admin/profile_qualifications/${id}`)
        .then(response => commit('set', response.data))
    },
    create({ dispatch }, profileQualification) {
      this.$authorize('create', 'ProfileQualification')

      return Vue.$http.post('admin/profile_qualifications', { profileQualification }).then(
        response => {
          dispatch('search', {
            filters: { profile: profileQualification.profile.id },
            sortBy: 'endsAt',
            descending: true,
          })
          return response.data
        },
        error => {
          return Object.assign(profileQualification, getErrorData(error))
        }
      )
    },
    update({ commit }, profileQualification) {
      this.$authorize('edit', 'ProfileQualification')

      return Vue.$http
        .patch(`admin/profile_qualifications/${profileQualification.id}`, {
          profileQualification,
        })
        .then(
          response => {
            commit('set', response.data)
            return response
          },
          error => {
            const errorData = getErrorData(error)

            commit('set', Object.assign(profileQualification, errorData))
            throw errorData
          }
        )
    },
    verify({ commit }, profileQualification) {
      this.$authorize('verify', 'ProfileQualification')

      return Vue.$http.patch(`admin/profile_qualifications/${profileQualification.id}/verify`).then(
        response => {
          commit('set', response.data)
          commit(
            'snackbar',
            { text: 'You have verified this profile qualification!', type: 'success' },
            { root: true }
          )
          return response.data
        },
        error => {
          const errorData = getErrorData(error)

          commit('set', Object.assign(profileQualification, errorData))
          commit(
            'snackbar',
            {
              text:
                'There was a problem verifying the profile qualification. Try again or contact the technical team if the problem persists.',
              type: 'error',
            },
            { root: true }
          )
          return errorData
        }
      )
    },
    destroy({ commit }, id) {
      this.$authorize('delete', 'ProfileQualification')

      return Vue.$http
        .delete(`admin/profile_qualifications/${id}`)
        .then(response => commit('remove', id))
    },
    createGrade({ commit }, profileQualification) {
      this.$authorize('edit', 'ProfileQualification')

      Vue.$http
        .post('admin/grades', { profileQualification })
        .then(({ data: grade }) => commit('set', Object.assign(profileQualification, { grade })))
    },
    removeGrade({ dispatch }, id) {
      this.$authorize('edit', 'ProfileQualification')

      Vue.$http.delete(`admin/grades/${id}`)
    },
  },
}
